import { DateTime } from 'luxon';

export const SCHEDULED_DOWNTIME_TIME_ZONE = 'Europe/Stockholm';

export const SCHEDULED_DOWNTIME_START = DateTime.fromISO(
  '2026-10-26T00:00:00',
  { zone: SCHEDULED_DOWNTIME_TIME_ZONE },
);

export const SCHEDULED_DOWNTIME_END = DateTime.fromISO('2026-10-27T00:00:00', {
  zone: SCHEDULED_DOWNTIME_TIME_ZONE,
});

export const SCHEDULED_DOWNTIME_ALLOWED_PATHS = [
  '/downtime',
  '/accessibility',
] as const;

const STAFF_EMAIL_DOMAIN = '@allbinary.se';

// Staff must be able to sign in (and out) as administrators to get past the
// downtime gate, so the admin login and logout pages of every tenant stay open.
const STAFF_AUTH_PATH = /^\/[^/]+\/log(?:in|out)$/;

export type ScheduledDowntimePhase = 'before' | 'active' | 'after';

const toZonedDateTime = (now: Date): DateTime =>
  DateTime.fromJSDate(now, { zone: SCHEDULED_DOWNTIME_TIME_ZONE });

export const normalizePathname = (pathname: string): string =>
  pathname.replace(/\/+$/, '') || '/';

export const isAllowedDuringScheduledDowntime = (pathname: string): boolean => {
  const normalized = normalizePathname(pathname);

  return (
    (SCHEDULED_DOWNTIME_ALLOWED_PATHS as readonly string[]).includes(
      normalized,
    ) || STAFF_AUTH_PATH.test(normalized)
  );
};

export const isScheduledDowntimeStaff = (email: string | undefined): boolean =>
  email?.trim().toLowerCase().endsWith(STAFF_EMAIL_DOMAIN) ?? false;

export const getScheduledDowntimePhase = (
  now: Date = new Date(),
): ScheduledDowntimePhase => {
  const current = toZonedDateTime(now);

  if (current < SCHEDULED_DOWNTIME_START) {
    return 'before';
  }

  if (current < SCHEDULED_DOWNTIME_END) {
    return 'active';
  }

  return 'after';
};

export const isScheduledDowntime = (now: Date = new Date()): boolean =>
  getScheduledDowntimePhase(now) === 'active';

export const getMsUntilScheduledDowntimeChange = (
  now: Date = new Date(),
): number | null => {
  const current = toZonedDateTime(now);

  if (current < SCHEDULED_DOWNTIME_START) {
    return SCHEDULED_DOWNTIME_START.diff(current).toMillis();
  }

  if (current < SCHEDULED_DOWNTIME_END) {
    return SCHEDULED_DOWNTIME_END.diff(current).toMillis();
  }

  return null;
};
